import type { AsMutableArray, MutableOrImmutableArray } from "../array-checks.js";
import type { Primitive } from "../primitive-types.js";

type _excludeFromTuple<TOrigRemaining extends MutableOrImmutableArray<any>, TExclude extends readonly any[], TBuilt extends MutableOrImmutableArray<any> = []> =
  AsMutableArray<TOrigRemaining> extends [infer FirstPart, ... infer Remaining]
  ? (
    TOrigRemaining extends readonly any[]
      ? (
        FirstPart extends TExclude[number] 
          ? _excludeFromTuple<readonly [...Remaining], TExclude, readonly [...TBuilt]>
          : _excludeFromTuple<readonly [...Remaining], TExclude, readonly [...TBuilt, FirstPart]>
      )
      : (
        FirstPart extends TExclude[number] ?
          _excludeFromTuple<Remaining, TExclude, TBuilt>
          : _excludeFromTuple<Remaining, TExclude, [...TBuilt, FirstPart]>
      )
  )
  : TBuilt;

export type ExcludeFromTuple<T extends MutableOrImmutableArray<any>, TExclude extends readonly any[]> = _excludeFromTuple<T, TExclude>;


export function excludeFrom<
  const A extends Primitive[] | readonly Primitive[],
  const Exclusions extends readonly (A[ number ])[]
>(arr: A, ...exclusions: Exclusions): ExcludeFromTuple<A, Exclusions>;

export function excludeFrom<
  const A extends Primitive[] | readonly Primitive[],
  const Exclusions extends readonly (A[ number ] | Primitive)[]
>(arr: A, ...exclusions: Exclusions): ExcludeFromTuple<A, Exclusions> {
  const result = arr.filter((a) => !exclusions.includes(a));

  if (Object.isFrozen(arr)){
    Object.freeze(result);
  }
  return result as ExcludeFromTuple<A, Exclusions>;
}